import { ImBin } from "react-icons/im";
import InfiniteScroll from "react-infinite-scroll-component";
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Nav, NavItem, NavLink, Input, Button, Card, CardBody, CardTitle } from 'reactstrap';
import Modal from "../../components/Modal";
import { AppContext } from '../../store/AppContext';
import CategoryList from "../../components/CategoryList";
import DetailsModal from "../../components/DetailsModal";
import { getUnpublishedVideos, approveRecord, rejectRecord, createCategories, deleteCategory, getCategories } from "../../Services";

import './admin.scss';

const PAGE_SIZE = 12;

const Admin = () => {
    const { userDetails } = useContext(AppContext);
    const [isAccept, setAcceptStatus] = useState(false);
    const [isReject, setRejectStatus] = useState(false);
    const [isConfirmationActive, setConfirmationStatus] = useState(false);
    const [activeTab, setActiveTab] = useState(1);
    const [newCategory, setNewCategory] = useState('');
    const { categories, setCategories } = useContext(AppContext);
    const [data, setData] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [lastKey, setLastKey] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [loading, setLoading] = useState(false);
    const fetchGen = useRef(0);

    // Seed category from full list; Admin CategoryList may override to weighted list.
    useEffect(() => {
        if (!selectedCategory && categories.length > 0) {
            setSelectedCategory(categories[0]);
        }
    }, [categories, selectedCategory]);

    const loadPending = useCallback(async (category, { append = false, cursor = null } = {}) => {
        if (!category) {
            setData([]);
            setHasMore(false);
            setLastKey(null);
            return;
        }

        const gen = ++fetchGen.current;
        setLoading(true);
        try {
            const response = await getUnpublishedVideos(
                category,
                PAGE_SIZE,
                cursor
            );
            if (gen !== fetchGen.current) {
                return;
            }
            const items = response?.items || [];
            setData((prev) => (append ? [...prev, ...items] : items));
            const next = response?.lastEvaluatedKey || null;
            setLastKey(next);
            setHasMore(Boolean(next?.key));
        } finally {
            if (gen === fetchGen.current) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        if (activeTab !== 1 || !selectedCategory) {
            return;
        }
        setData([]);
        setLastKey(null);
        setHasMore(false);
        loadPending(selectedCategory, { append: false });
    }, [selectedCategory, activeTab, loadPending]);

    const fetchMoreData = () => {
        if (!hasMore || !lastKey?.key || loading) {
            return;
        }
        loadPending(selectedCategory, { append: true, cursor: lastKey.key });
    };

    const getModalTitle = () => {
        if (isAccept) {
            return 'Confirm Acceptance';
        } else if (isReject) {
            return 'Confirm Rejection';
        } else {
            return '';
        }
    };

    const getModalContent = () => {
        if (isAccept) {
            return 'Are you sure you want to accept this request?';
        } else if (isReject) {
            return 'Are you sure you want to reject this request?'
        } else {
            return '';
        }
    };

    const onConfirm = async () => {
        if (isAccept) {
            await approveRecord({
                category: selectedCategory,
                key: selectedId.key
            });
        } else if (isReject) {
            await rejectRecord({
                category: selectedCategory,
                key: selectedId.key
            });
        }

        setSelectedId(null);
        await loadPending(selectedCategory, { append: false });
        onClose();
    };

    const onClose = () => {
        setAcceptStatus(false);
        setRejectStatus(false);
        setConfirmationStatus(false);
    };

    const onAcceptClick = () => {
        setAcceptStatus(true);
        setConfirmationStatus(true);
    };

    const onRejectClick = () => {
        setRejectStatus(true);
        setConfirmationStatus(true);
    };

    const onAddNewCategory = async () => {
        let response = await createCategories({ categoryName: newCategory });
        if (response && response.categoryNames && Array.isArray(response.categoryNames)) {
            setNewCategory('')
            setCategories(response.categoryNames.sort());
        }
    };

    const onDeleteCategory = async (name) => {
        let response = await deleteCategory({ categoryName: name });
        if (response === "OK") {
            let data = await getCategories();
            if (data && data.categoryNames && Array.isArray(data.categoryNames)) {
                setCategories(data.categoryNames.sort());
            }
        }
    };

    const onCardClick = (item) => {
        setSelectedId(item);
    };

    // When weighted pending categories arrive, pick the first one if current is empty
    // or not among categories that have pending videos.
    const onPendingCategories = useCallback((weightedCats) => {
        if (!Array.isArray(weightedCats) || weightedCats.length === 0) {
            return;
        }
        setSelectedCategory((current) => {
            if (current && weightedCats.includes(current)) {
                return current;
            }
            return weightedCats[0];
        });
    }, []);

    return (
        <div className='container admin-page'>
            <Nav tabs>
                <NavItem>
                    <NavLink
                        className={activeTab === 1 ? "active" : ""}
                        onClick={() => setActiveTab(1)}
                    >
                        Videos pending for Approval
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={activeTab === 2 ? "active" : ""}
                        onClick={() => setActiveTab(2)}
                    >
                        Create new Category
                    </NavLink>
                </NavItem>
            </Nav>
            {activeTab === 1 && <>
                {
                    isConfirmationActive && <Modal
                        className="confirmation"
                        title={getModalTitle()}
                        content={getModalContent()}
                        primary="Confirm"
                        secondary="Cancel"
                        primaryClick={onConfirm}
                        secondaryClick={onClose} />
                }
                {selectedId !== null && <DetailsModal
                    id={selectedId.key}
                    title={selectedId.videoName}
                    viewCount={selectedId.viewCount}
                    content={selectedId.description}
                    uploadedDate={selectedId.dateUploaded}
                    researchId={selectedId.researchId}
                    attachment={selectedId.attachmentKey}
                    onClose={() => setSelectedId(null)}
                    isApproval={true}
                    onAccept={onAcceptClick}
                    onReject={onRejectClick}
                    category={selectedCategory}
                    isDeletable={userDetails?.role === "ADMIN"}
                    hideRelaventVideos={true}
                />}
                <CategoryList
                    active={selectedCategory}
                    onCategorySelection={setSelectedCategory}
                    AdminPage={true}
                    onCategoriesLoaded={onPendingCategories}
                />
                <div className="content-container">
                    <InfiniteScroll
                        dataLength={data.length}
                        next={fetchMoreData}
                        hasMore={hasMore}
                    >
                        {data.map((item) => (
                            <Card
                                key={item.key}
                                title={item.videoName}
                                style={{
                                    cursor: "pointer",
                                    maxHeight: "350px"
                                }}
                                onClick={() => onCardClick(item)}
                            >
                                <div>
                                    <img alt={item.videoName} src={item.imageKey} />
                                </div>
                                <CardBody style={{ height: "20%" }} onClick={() => onCardClick(item)} title={item.videoName}>
                                    <CardTitle tag="h5" title={item.videoName}>{item.videoName}</CardTitle>
                                    <CardTitle tag="h6" title={item.viewCount}>{`${item.viewCount ?? 0} views`}</CardTitle>
                                </CardBody>
                            </Card>
                        ))}
                        {
                            !loading && data.length === 0 ? (
                                <div className="no-records">No record found!</div>
                            ) : null
                        }
                        {
                            loading && data.length === 0 ? (
                                <div className="no-records">Loading pending videos…</div>
                            ) : null
                        }
                    </InfiniteScroll>
                </div>
            </>}
            {
                activeTab === 2 && (
                    <div className="category-admin-row">
                        <div className='available-category'>
                            <h5>Available Categories</h5>
                            <div className='category-list'>
                                {categories.map((item) =>
                                    <div key={item}>
                                        {item}
                                        <ImBin
                                            title='Delete Category'
                                            onClick={() => onDeleteCategory(item)}
                                        />
                                    </div>)
                                }
                            </div>
                        </div>
                        <div className='add-category'>
                            <h5>Add New</h5>
                            <Input
                                type="text"
                                name="addCategory"
                                id="addCategory"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                                placeholder="New Category"
                            />
                            <Button disabled={newCategory === ""} className='add-action' color="primary" onClick={onAddNewCategory}>Add</Button>
                        </div>
                    </div>
                )
            }
        </div>
    );
}

export default Admin;
